import React from 'react'
import type { DataSet } from '../../../core/types/InsightsInstanceSlotData'

interface BarStyleProps extends React.CSSProperties {
  x?: number
  y?: number
}

interface CustomDoubleBarPlotMXUIProps {
  color: string
  data: DataSet[]
  series: Series
  style: BarStyleProps
}

interface Series {
  index: number
  type: number
}

export const CustomDoubleBarPlotMXUI = ({
  color,
  data,
  series,
  style,
  ...restProps
}: CustomDoubleBarPlotMXUIProps) => {
  const rectRef = React.useRef<SVGRectElement>(null)

  React.useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        if (rectRef.current) {
          const { width } = rectRef.current.getBoundingClientRect()

          const xValue = width / 2
          const textElement = rectRef.current.querySelector('text')
          if (textElement) {
            textElement.setAttribute('transform', `translate(${xValue}, -8)`)
          }
        }
      })
    })

    if (rectRef.current) resizeObserver.observe(rectRef.current)
    return () => {
      if (rectRef.current) resizeObserver.unobserve(rectRef.current)
    }
  }, [])

  const { ariaLabelComparison, ariaLabelMain } = data[series.index]

  const ariaLabel = series.type === 0 ? ariaLabelMain : ariaLabelComparison

  const rectStyle = {
    ...style,
    transition: 'all 0.3s ease-in-out',
  }

  // Create path with rounded top corners (10px radius) and square bottom corners
  const pathBarWidth = (style.width as number) || 0
  const pathBarHeight = (style.height as number) || 0
  const pathX = (style.x as number) || 0
  const pathY = (style.y as number) || 0
  const radius = 4

  const pathData = `
    M ${pathX + radius},${pathY}
    L ${pathX + pathBarWidth - radius},${pathY}
    Q ${pathX + pathBarWidth},${pathY} ${pathX + pathBarWidth},${pathY + radius}
    L ${pathX + pathBarWidth},${pathY + pathBarHeight}
    L ${pathX},${pathY + pathBarHeight}
    L ${pathX},${pathY + radius}
    Q ${pathX},${pathY} ${pathX + radius},${pathY}
    Z
  `.trim()

  return (
    <g aria-label={ariaLabel}>
      <path {...restProps} d={pathData} fill={color} style={rectStyle} />
    </g>
  )
}
